import { useCallback, useMemo, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { type UseFormReturn, useForm } from "react-hook-form";

import { useGetMargin, useGetPositions } from "~/v2/api/hooks";
import {
  useCryptoWalletAPI,
  useGetCryptoAddress,
  useGetCryptoAssets,
} from "~/v2/api/hooks/useGetCrypto";
import { queryKeys } from "~/v2/api/queryKeys";
import type { Account, Product } from "~/v2/api/rest/account";
import type { CryptoAsset } from "~/v2/api/rest/cryptoWallet";
import type { CryptoSelectOption } from "../presentation/CryptoSelect";
import {
  createAssetMap,
  getChainIconKey,
  groupAssetsByChain,
  resolveAssetChainCell,
} from "../util";
import {
  CREATE_TRANSFER_DEFAULTS,
  type CreateTransferFormValues,
  refineCreateTransfer,
  schema,
  type Tab,
} from "./schema";
import { useCryptoTransferSymbols } from "./useCryptoTransferSymbols";
import { type UseDepositReturn, useDeposit } from "./useDeposit";
import { type UseWithdrawReturn, useWithdraw } from "./useWithdraw";

export interface UseTransferCardControllerProps {
  account: Account | undefined;
  accountId: string;
  product: Product | undefined;
  isFetchingEnabled: boolean;
  onAddWhitelistAddressClicked: () => void;
}

export interface SelectedAssetDisplay {
  chainLabel: string;
  assetLabel: string;
  chainIconKey: string;
  assetIconKey: string;
}

export interface TransferCardControllerReturn {
  tab: Tab;
  onTabChanged: (nextTab: Tab) => void;
  asset: CryptoAsset | null;
  assetError: string | null;
  chain: string;
  assetCode: string;
  selectedAssetDisplay: SelectedAssetDisplay;
  chainOptions: CryptoSelectOption[];
  assetOptions: CryptoSelectOption[];
  isAssetDisabled: boolean;
  onChainChanged: (nextChain: string) => void;
  onAssetChanged: (nextAssetCode: string) => void;
  fee: string;
  withdraw: UseWithdrawReturn["withdraw"];
  deposit: UseDepositReturn;
  onAddWhitelistAddressClicked: () => void;
}

export const useTransferCardController = ({
  account,
  accountId,
  product,
  isFetchingEnabled,
  onAddWhitelistAddressClicked,
}: UseTransferCardControllerProps): TransferCardControllerReturn => {
  const queryClient = useQueryClient();

  const { margin } = useGetMargin({
    accountID: accountId,
    paper: false,
    options: {
      enabled: !!accountId,
    },
  });

  const positions = useGetPositions({
    accountID: accountId,
    product,
    options: { enabled: isFetchingEnabled },
  });

  const assets = useGetCryptoAssets({
    accountId,
    options: {
      enabled: isFetchingEnabled,
    },
  });

  const [tab, setTab] = useState<Tab>("deposit");
  const { chainOptions, assetOptionsByChain } = useMemo(
    () => groupAssetsByChain(assets.data ?? []),
    [assets.data],
  );

  const supportedAssetsByChain = useMemo(() => {
    const result: Record<string, Set<string>> = {};
    for (const [chainCode, options] of Object.entries(assetOptionsByChain)) {
      result[chainCode] = new Set(options.map((option) => option.value));
    }
    return result;
  }, [assetOptionsByChain]);

  const whitelistAddressesRef = useRef<string[]>([]);
  const withdrawableAmountRef = useRef(0);
  const feeAmountRef = useRef<number | null>(null);

  const resolver = useMemo(
    () =>
      zodResolver(
        schema.superRefine((values, issues) =>
          refineCreateTransfer({
            tab,
            supportedAssetsByChain,
            whitelistAddresses: whitelistAddressesRef.current,
            withdrawableAmount: withdrawableAmountRef.current,
            feeAmount: feeAmountRef.current,
          })(values, issues),
        ),
      ),
    [tab, supportedAssetsByChain],
  );

  const {
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  }: UseFormReturn<CreateTransferFormValues> = useForm<CreateTransferFormValues>(
    {
      resolver,
      mode: "onSubmit",
      reValidateMode: "onChange",
      defaultValues: CREATE_TRANSFER_DEFAULTS,
    },
  );

  const chain = watch("chain");
  const assetCode = watch("asset");
  const address = watch("address");
  const amountStr = watch("amount");

  const assetOptions = useMemo(
    () => (chain ? (assetOptionsByChain[chain] ?? []) : []),
    [chain, assetOptionsByChain],
  );

  const isAssetDisabled = !chain;

  const asset = useMemo(() => {
    if (!chain || !assetCode) {
      return null;
    }
    return (
      (assets.data ?? []).find(
        (a) => a.chain?.chain === chain && a.asset === assetCode,
      ) ?? null
    );
  }, [assets.data, chain, assetCode]);

  const { walletSymbol, quoteSymbol, positionSymbol } =
    useCryptoTransferSymbols(asset);

  const assetMap = useMemo(
    () => createAssetMap(assets.data ?? []),
    [assets.data],
  );

  const selectedAssetDisplay = useMemo<SelectedAssetDisplay>(() => {
    if (!chain || !assetCode) {
      return {
        chainLabel: "",
        assetLabel: "",
        chainIconKey: "",
        assetIconKey: "",
      };
    }
    const { chainLabel, assetLabel, iconKey } = resolveAssetChainCell({
      asset: assetCode,
      chain,
      assetMap,
    });
    return {
      chainLabel,
      assetLabel,
      chainIconKey: getChainIconKey(chain),
      assetIconKey: iconKey,
    };
  }, [chain, assetCode, assetMap]);

  const {
    data: depositAddress,
    isLoading: isDepositAddressLoading,
    isError: isDepositAddressError,
    refetch: refetchDepositAddress,
  } = useGetCryptoAddress({
    accountId,
    symbol: walletSymbol,
    options: {
      enabled: isFetchingEnabled && !!walletSymbol,
    },
  });

  const cryptoWalletAxiosInstance = useCryptoWalletAPI();
  const invalidateTransfers = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.crypto.transfers(accountId),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.positions.list(accountId, product),
    });
  }, [accountId, product, queryClient]);

  const {
    withdraw,
    whitelistAddresses,
    withdrawableAmount,
    feeAmount,
    resetWithdraw,
    cancelConfirmation,
  } = useWithdraw({
    accountId,
    isFetchingEnabled,
    setValue,
    reset,
    handleSubmit,
    errors,
    chain,
    assetCode,
    address,
    amountStr,
    asset,
    walletSymbol,
    quoteSymbol,
    positionSymbol,
    positions: positions.positions ?? [],
    cryptoWalletAxiosInstance,
    invalidateTransfers,
    depositFromAddress: depositAddress?.address ?? "",
  });

  whitelistAddressesRef.current = whitelistAddresses;
  withdrawableAmountRef.current = withdrawableAmount;
  feeAmountRef.current = feeAmount;

  const deposit = useDeposit({
    account,
    margin: margin ?? undefined,
    asset,
    depositAddress,
    isDepositAddressLoading,
    isDepositAddressError,
    refetchDepositAddress,
  });

  const onTabChanged = (nextTab: Tab) => {
    setTab(nextTab);
    resetWithdraw();
  };

  const onChainChanged = (nextChain: string) => {
    setValue("chain", nextChain, { shouldValidate: false, shouldDirty: true });
    setValue("asset", "", { shouldValidate: false, shouldDirty: true });
    setValue("address", "", { shouldValidate: false, shouldDirty: true });
    setValue("amount", "", { shouldValidate: false, shouldDirty: true });
    cancelConfirmation();
  };

  const onAssetChanged = (nextAssetCode: string) => {
    setValue("asset", nextAssetCode, {
      shouldValidate: false,
      shouldDirty: true,
    });
    setValue("address", "", { shouldValidate: false, shouldDirty: true });
    setValue("amount", "", { shouldValidate: false, shouldDirty: true });
    cancelConfirmation();
  };

  return {
    tab,
    onTabChanged,
    asset,
    assetError: errors.asset?.message ?? null,
    chain,
    assetCode,
    selectedAssetDisplay,
    chainOptions,
    assetOptions,
    isAssetDisabled,
    onChainChanged,
    onAssetChanged,
    fee: withdraw.fee,
    withdraw,
    deposit,
    onAddWhitelistAddressClicked,
  };
};
